import React, { useState } from 'react';
import { X, FileDown } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import BatchIndentPDF from './BatchIndentPDF';
import * as XLSX from 'xlsx';

export const ExportPopup = ({ onClose, batch, customFilename = 'batch-indent' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async (format) => {
    setIsLoading(true);
    setError('');
    
    try {
      if (!batch) {
        throw new Error('No batch data available for export');
      }
      
      if (format === 'pdf') {
        const blob = await pdf(
          <BatchIndentPDF batch={batch} />
        ).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${customFilename}-${batch.BatchNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        onClose();
        return;
      }
      
      // For Excel and CSV, use XLSX library
      if (format === 'csv' || format === 'xlsx') {
        // Prepare data for export
        const exportData = batch.Indents.map((indent, index) => ({
          'S.No': index + 1,
          'Indent ID': indent.IndentID,
          'Project Name': indent.ProjectName,
          'Location': indent.Location,
          'Created Date': new Date(indent.CreationDate).toLocaleString(),
          'Status': indent.Status
        }));

        if (format === 'csv') {
          const ws = XLSX.utils.json_to_sheet(exportData);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Batch Indents');
          XLSX.writeFile(wb, `${customFilename}-${batch.BatchNumber}.csv`);
        } else {
          const ws = XLSX.utils.json_to_sheet(exportData);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Batch Indents');
          XLSX.writeFile(wb, `${customFilename}-${batch.BatchNumber}.xlsx`);
        }
        
        onClose();
        return;
      }
      
      throw new Error(`Unsupported export format: ${format}`);
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export data: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Export Batch</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
          <button
            onClick={() => handleExport('csv')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as CSV
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as PDF
          </button>
        </div>
      </div>
    </div>
  );
}; 